import { t } from '@bia-auction/shared-i18n';
import {
  CHECKLIST_ITEMS,
  EQUIPMENT_KINDS,
  EXPERTISE_PARTS,
  type EquipmentDefinition,
  equipmentDefinitionSchema,
  type LotDetail,
  lotDetailSchema,
  type SaleLot,
  type ServerEvent,
} from '@bia-auction/shared-schema';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatusBadge,
  lotStatusVariant,
  toast,
} from '@bia-auction/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { CheckCircle2, Fuel, Gauge, Settings2, Timer, CalendarDays } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';
import { ErrorMsg, Loading } from '../components/Feedback';
import { Gallery } from '../components/Gallery';
import { LotActions } from '../components/LotActions';
import { MaskedPrice } from '../components/MaskedPrice';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCountdown, formatDate } from '../lib/format';
import { bypassesDeposit } from '../lib/roles';
import { useSaleRealtime } from '../lib/ws';
import {
  bodyTypeLabel,
  conditionLabel,
  equipmentKindLabel,
  expertisePartLabel,
  expertiseStateLabel,
  fuelLabel,
  gearLabel,
  vehicleName,
  vehicleTypeLabel,
} from '../lib/vehicle-labels';
import { useNow } from '../lib/useNow';

/**
 * The vehicle detail page (M11, redesign-vehicle.md §7) — the page the legacy product had and
 * Bia lost with the stock slim-down: gallery, spec sheet, description, equipment, and the
 * paint/replacement report with its "Orijinal" badge.
 *
 * **Members only** ([ADR 0027](../../../../docs/adr/0027-login-gated-auction-surface.md)). M11
 * opened this page to anonymous visitors behind a server-side money mask; the 13.08.2026 product
 * decision closed it — a logged-out visitor is redirected to `/login` by the route guard, and the
 * API answers 401 regardless.
 *
 * A signed-in visitor can **bid from here** — §7's approved mock puts `[ Teklif ver ]` in the
 * price panel, and sending someone back to the room to press it loses the car they were
 * looking at. That makes the price live, so this page subscribes to the lot's realtime stream
 * exactly as the room does.
 */
export function VehicleDetailPage() {
  const { auctionId, lotId } = useParams({ from: '/shell/auctions/$auctionId/lots/$lotId' });
  const { state } = useAuth();
  const now = useNow();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['lot', auctionId, lotId],
    queryFn: () => api<LotDetail>(`/auctions/${auctionId}/lots/${lotId}`, {}, lotDetailSchema),
  });
  // Only for naming the selected equipment. Still gated on the session: the catalogue is an
  // `/admin/*` read, and a session that lapses mid-page must not turn a 401 here into a failed
  // page — the group falls back to the raw slugs instead.
  const equipment = useQuery({
    queryKey: ['equipment-definitions'],
    enabled: state.authenticated,
    queryFn: () => api('/admin/equipment-definitions', {}, z.array(equipmentDefinitionSchema)),
  });

  // WS-delivered updates are held as an **overlay merged over** the REST snapshot at render
  // time, never mutated into it — the same rule the room uses (AuctionDetailPage): a refetch
  // that read D1 just before a bid committed can land after the WS frame, and the realtime
  // truth must win over the stale snapshot.
  const [overlay, setOverlay] = useState<Partial<SaleLot>>({});
  const refetch = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['lot', auctionId, lotId] });
  }, [qc, auctionId, lotId]);
  // A (re)connect is the reconciliation point: the fresh snapshot reflects anything missed
  // during the outage, so the overlay is dropped and re-applied by events that follow.
  const reconcileOnReconnect = useCallback(() => {
    setOverlay({});
    refetch();
  }, [refetch]);

  const onEvent = useCallback(
    (ev: ServerEvent) => {
      // The socket also carries the sale room and this user's stream; a frame about a sibling
      // lot is not this page's business. Sale-level lifecycle frames carry no `lotId`.
      if ('lotId' in ev && ev.lotId && ev.lotId !== lotId) return;
      const patch = (p: Partial<SaleLot>) => setOverlay((o) => ({ ...o, ...p }));
      switch (ev.type) {
        case 'bid.placed':
          patch({
            currentTopBid: ev.amount,
            bidCount: ev.bidCount,
            reserveCrossed: ev.priceGoalReached,
            // See AuctionDetailPage: the step moves with the price, so it has to travel with
            // the event or the confirmed figure goes stale (M19).
            ...(ev.nextIncrement !== undefined ? { increment: ev.nextIncrement } : {}),
          });
          break;
        case 'lot.opened':
          patch({ state: 'OPEN', endDate: ev.endsAt });
          break;
        case 'lot.extended':
          patch({ endDate: ev.newEndsAt });
          break;
        case 'lot.closed':
          patch({ state: ev.finalState });
          break;
        case 'lot.removed':
          patch({ state: 'REMOVED' });
          break;
        case 'sale.resumed': {
          // Resuming recomputes the serial tail; take this lot's new window straight from the
          // frame so the countdown doesn't stay stale while the detail refetches.
          const mine = ev.reschedule.find((r) => r.lotId === lotId);
          if (mine) patch({ startDate: mine.newStartDate, endDate: mine.newEndDate });
          refetch();
          break;
        }
        case 'sale.paused':
        case 'sale.closed':
          // Refetch so `saleState` (→ PAUSED/CLOSED) gates the controls below.
          refetch();
          break;
        case 'outbid':
          toast(t('bidder.detail.outbid'), 'error');
          break;
        case 'you-won':
          toast(t('bidder.detail.won'), 'success');
          break;
      }
    },
    [lotId, refetch],
  );

  const lotRooms = useMemo(() => [`lot:${lotId}`], [lotId]);
  // Fingerprint of the authenticated identity + roles — a change re-opens the socket so it
  // re-verifies the fresh token at the upgrade (see `useSaleRealtime`).
  const authKey = `${state.userId ?? ''}|${[...state.roles].sort().join(',')}`;
  useSaleRealtime(
    state.authenticated ? auctionId : null,
    lotRooms,
    onEvent,
    reconcileOnReconnect,
    authKey,
  );

  if (isLoading) return <Loading />;
  if (error) return <ErrorMsg error={error} />;
  if (!data) return null;

  const { vehicle } = data;
  const lot = { ...data.lot, ...overlay };
  const title = vehicleName(vehicle);
  const remaining = lot.endDate - now;
  const saleActive = data.saleState !== 'PAUSED' && data.saleState !== 'CLOSED' && data.saleState !== 'CANCELLED';
  const isOpen = saleActive && lot.state === 'OPEN' && remaining > 0;
  const photos = vehicle.media.filter((m) => m.kind === 'photo' || m.kind === 'video');
  const damage = vehicle.media.filter((m) => m.kind === 'damage_photo' || m.kind === 'damage_video');

  const specs: Array<[string, string]> = [];
  const push = (label: string, value: string | number | null | undefined, suffix = '') => {
    if (value !== null && value !== undefined && value !== '') specs.push([label, `${value}${suffix}`]);
  };
  push(t('vehicles.field.plate'), vehicle.plate);
  push(t('vehicles.field.brand'), vehicle.brand);
  push(t('vehicles.field.series'), vehicle.series);
  push(t('vehicles.field.modelName'), vehicle.modelName);
  push(t('vehicles.field.color'), vehicle.color);
  if (vehicle.vehicleType) specs.push([t('vehicles.field.vehicleType'), vehicleTypeLabel(vehicle.vehicleType)]);
  if (vehicle.bodyType) specs.push([t('vehicles.field.bodyType'), bodyTypeLabel(vehicle.bodyType)]);
  push(t('vehicles.field.km'), vehicle.km?.toLocaleString('tr-TR'), ' km');
  push(t('vehicles.field.year'), vehicle.year);
  push(t('vehicles.field.engineCapacity'), vehicle.engineCapacity, ' cc');
  push(t('vehicles.field.enginePower'), vehicle.enginePower, ' hp');
  if (vehicle.gear) specs.push([t('vehicles.field.gear'), gearLabel(vehicle.gear)]);
  if (vehicle.fuel) specs.push([t('vehicles.field.fuel'), fuelLabel(vehicle.fuel)]);
  if (vehicle.condition) specs.push([t('vehicles.field.condition'), conditionLabel(vehicle.condition)]);

  const equipmentById = new Map((equipment.data ?? []).map((e: EquipmentDefinition) => [e.id, e]));
  const checklistYes = CHECKLIST_ITEMS.filter((item) => vehicle.checklist?.[item] === true);

  return (
    <div className="space-y-6">
      <Link
        to="/auctions/$auctionId"
        params={{ auctionId }}
        className="text-sm text-muted-foreground hover:underline"
      >
        {t('bidder.vehicle.backToAuction')}
      </Link>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-6">
          <Gallery media={photos} alt={title} />

          {/* Genel Bakış — the legacy page's four icon stats, then the description. */}
          <Card>
            <CardContent className="space-y-4 p-5">
              <ul className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Stat icon={CalendarDays} label={t('vehicles.field.year')} value={vehicle.year?.toString()} />
                <Stat
                  icon={Gauge}
                  label={t('vehicles.field.km')}
                  value={vehicle.km !== null ? `${vehicle.km.toLocaleString('tr-TR')}` : undefined}
                />
                <Stat
                  icon={Settings2}
                  label={t('vehicles.field.gear')}
                  value={vehicle.gear ? gearLabel(vehicle.gear) : undefined}
                />
                <Stat
                  icon={Fuel}
                  label={t('vehicles.field.fuel')}
                  value={vehicle.fuel ? fuelLabel(vehicle.fuel) : undefined}
                />
              </ul>
              {vehicle.description && (
                <p className="whitespace-pre-line text-sm leading-relaxed">{vehicle.description}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('vehicles.section.technical')}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                {specs.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b py-1 text-sm">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="tabular-nums">{v}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>

          {vehicle.equipments && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('vehicles.section.equipment')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {EQUIPMENT_KINDS.map((kind) => {
                  const ids = vehicle.equipments?.[kind] ?? [];
                  // Empty groups are skipped (ADR 0025) — an empty heading reads as a bug.
                  if (ids.length === 0) return null;
                  return (
                    <div key={kind}>
                      <h3 className="mb-1 text-sm font-semibold">{equipmentKindLabel(kind)}</h3>
                      <ul className="grid gap-1 text-sm sm:grid-cols-2 lg:grid-cols-3">
                        {ids.map((id) => (
                          <li key={id} className="flex items-center gap-1.5">
                            <CheckCircle2 aria-hidden className="h-3.5 w-3.5 text-success" />
                            {equipmentById.get(id)?.nameTr ?? id}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {checklistYes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('checklist.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="grid gap-1 text-sm sm:grid-cols-2 lg:grid-cols-3">
                  {checklistYes.map((item) => (
                    <li key={item} className="flex items-center gap-1.5">
                      <CheckCircle2 aria-hidden className="h-3.5 w-3.5 text-success" />
                      {t(`checklist.${item}`)}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {vehicle.expertise && Object.keys(vehicle.expertise).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t('expertise.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* The legacy disclaimer, verbatim — it is a liability statement, not copy. */}
                <p className="text-xs text-muted-foreground">{t('expertise.disclaimer')}</p>
                {vehicle.isOriginal && (
                  <StatusBadge variant="success">
                    <CheckCircle2 aria-hidden className="h-3.5 w-3.5" />
                    {t('expertise.originalBadge')}
                  </StatusBadge>
                )}
                <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
                  {EXPERTISE_PARTS.filter((p) => vehicle.expertise?.[p]).map((part) => (
                    <div key={part} className="flex justify-between gap-4 border-b py-1 text-sm">
                      <dt className="text-muted-foreground">{expertisePartLabel(part)}</dt>
                      <dd>{expertiseStateLabel(vehicle.expertise![part]!)}</dd>
                    </div>
                  ))}
                </dl>
                {damage.length > 0 && <Gallery media={damage} alt={t('media.kind.damagePhoto')} />}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Price + bid controls. Sticky beside the gallery on desktop; on a phone it is pulled
            **above** the gallery, because a bid button below a spec sheet and two galleries is
            a bid button nobody presses during a live lot. (§7's fixed bottom bar is the
            stronger answer and is still not built.) */}
        <aside className="-order-1 lg:order-none lg:sticky lg:top-4 lg:self-start">
          <Card>
            <CardContent className="space-y-3 p-5">
              <h1 className="font-head text-h2 font-semibold">{title}</h1>
              {vehicle.plate && (
                <p className="font-mono text-sm text-muted-foreground tabular-nums">{vehicle.plate}</p>
              )}
              <MaskedPrice
                amount={lot.currentTopBid ?? lot.startingPrice}
                masked={lot.priceMasked}
                currency={lot.currency}
                className="block font-head text-h1 font-semibold tabular-nums"
              />
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                <span className="tabular-nums">
                  {t('bidder.detail.bidCountShort', { n: lot.bidCount })}
                </span>
                {isOpen && (
                  <span className="flex items-center gap-1">
                    <Timer aria-hidden className="h-4 w-4" />
                    <span className="font-mono tabular-nums">{formatCountdown(remaining)}</span>
                  </span>
                )}
                {!isOpen && (
                  <StatusBadge variant={lotStatusVariant(lot.state)}>
                    {t(`lotState.${lot.state}`)}
                  </StatusBadge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {formatDate(lot.startDate, { weekday: true })}
              </p>
              {/* The same controls as the room's lot card, from one component — bidding must
                  not mean different things on two pages. */}
              <div className="grid gap-2 pt-1">
                <LotActions
                  saleId={auctionId}
                  lot={lot}
                  isOpen={isOpen}
                  saleActive={saleActive}
                  canDeposit={!bypassesDeposit(state.roles)}
                  authenticated={state.authenticated}
                  stacked
                />
                <Link
                  to="/auctions/$auctionId"
                  params={{ auctionId }}
                  className="pt-1 text-center text-sm text-muted-foreground hover:underline"
                >
                  {t('bidder.vehicle.goToRoom')}
                </Link>
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Gauge;
  label: string;
  value: string | undefined;
}) {
  return (
    <li className="flex items-center gap-2">
      <Icon aria-hidden className="h-5 w-5 text-tx-brand" />
      <span className="min-w-0">
        <span className="block text-xs text-muted-foreground">{label}</span>
        <span className="block truncate text-sm font-medium tabular-nums">
          {value ?? t('common.none')}
        </span>
      </span>
    </li>
  );
}
